import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import axios from "axios";

/**
 * Admin: Gift Sets (CRUD)
 * - Product live-search with picker
 * - Perfume ML selector
 * - Accessories: pick Color + Size → accessory_variant_id
 * - Price calc (fixed / % off / amount off)
 * - Image gallery (multi)
 * - Edit mode hydration
 */

const API = "https://admin.shopaholics.ge/api/gift_sets";

// ───────────── UI
const Wrap = styled.div`max-width:1100px;margin:20px auto;padding:0 16px;`;
const Top = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;`;
const H = styled.h2`margin:0;`;
const Btn = styled.button`
  height:38px;padding:0 12px;border-radius:10px;cursor:pointer;
  border:1px solid #e5e7eb;background:#111827;color:#fff;font-weight:800;
`;
const Ghost = styled(Btn)`background:#fff;color:#111827;`;
const Grid = styled.div`background:#fff;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;`;
const Row = styled.div`
  display:grid;
  grid-template-columns:60px 1.5fr 120px 100px 90px 110px 90px 130px;
  gap:8px;
  padding:10px 12px;
  align-items:center;
  border-top:1px solid #f1f1f3;

  &:first-child{
    background:#fafafa;
    font-weight:900;
  }

  @media (max-width:1000px){
    grid-template-columns:60px 1.3fr 100px 90px 80px 100px 80px 120px;
  }

  @media (max-width:760px){
    grid-template-columns:1fr;
    gap:6px;

    & > *{
      margin:2px 0;
    }
  }
`;
const Field = styled.div`display:grid;gap:6px;margin-bottom:12px;`;
const Input = styled.input`height:38px;border-radius:10px;border:1px solid #e5e7eb;padding:0 10px;`;
const Textarea = styled.textarea`min-height:80px;border-radius:10px;border:1px solid #e5e7eb;padding:10px;`;
const Select = styled.select`height:38px;border-radius:10px;border:1px solid #e5e7eb;padding:0 10px;`;
const Card = styled.div`border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin:16px 0;`;
const Small = styled.div`font-size:.9rem;color:#6b7280;`;
const Tag = styled.span`
  display:inline-flex;align-items:center;gap:6px;padding:4px 8px;border-radius:999px;border:1px solid #e5e7eb;margin-right:6px;
`;
const ProductsTable = styled.div`
  display:grid;gap:8px;margin-top:8px;
  > div{display:grid;grid-template-columns:1.2fr 0.6fr 1.2fr 0.6fr 0.4fr;gap:8px;align-items:center;}
  > div.head{font-weight:900;opacity:.8;}
  @media (max-width:980px){ > div { grid-template-columns:1.2fr 0.6fr 1.2fr 0.6fr 0.4fr; } }
  @media (max-width:720px){ > div { grid-template-columns:1fr; } }
`;
const Danger = styled.button`
  height:34px;padding:0 10px;border-radius:10px;cursor:pointer;
  border:1px solid #fee2e2;background:#fef2f2;color:#b91c1c;font-weight:800;
`;
const Ok = styled.button`
  height:34px;padding:0 10px;border-radius:10px;cursor:pointer;
  border:1px solid #d1fae5;background:#ecfdf5;color:#065f46;font-weight:800;
`;

const CATEGORIES = [
  { value: "perfume", label: "Perfume" },
  { value: "accessories", label: "Accessories" },
  { value: "skin-care", label: "Skin Care" },
  { value: "hair-and-body-care", label: "Hair & Body" },
];

// ───────────── Product Picker
const PickerWrap = styled.div`position:relative;`;
const SearchBox = styled.input`
  height:38px;border-radius:10px;border:1px solid #e5e7eb;padding:0 10px;width:100%;
`;
const Results = styled.div`
  position:absolute;z-index:20;top:42px;left:0;right:0;background:#fff;
  border:1px solid #e5e7eb;border-radius:10px;max-height:260px;overflow:auto;
`;
const RowItem = styled.div`
  padding:8px 10px;cursor:pointer;display:flex;gap:8px;align-items:center;
  &:hover{background:#f7f7f7;}
  small{color:#6b7280;} b{font-weight:800;}
`;

function ProductPicker({ value, onSelect }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!q) { setList([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search_products.php`, { params: { q, limit: 20 } });
        setList(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch (e) {
        console.error(e);
        setList([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && list.length) {
      onSelect(list[0]);
      setQ("");
      setOpen(false);
    }
  };

  return (
    <PickerWrap>
      <SearchBox
        placeholder={value ? `${value.name} (${value.brand})` : "Search by name or code…"}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (list.length) setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {value && <div style={{marginTop:6,fontSize:12,color:"#374151"}}>Selected: <b>{value.name}</b> (ID: {value.id})</div>}
      {open && list.length > 0 && (
        <Results onMouseLeave={() => setOpen(false)}>
          {list.map((p) => (
            <RowItem key={p.id} onClick={() => { onSelect(p); setQ(""); setOpen(false); }}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',gap:6,alignItems:'baseline'}}>
                  <b style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{p.name}</b>
                  <small>· {p.brand}</small>
                </div>
                <small>{p.category}{p.sex ? ` · ${p.sex}` : ""}</small>
              </div>
              {Array.isArray(p.milliliters) && p.milliliters.length ? (
                <small>{Math.min(...p.milliliters.map(m => Number(m.sale_price || m.price) || 0))} ₾+</small>
              ) : (
                <small>{p.sale_p > 0 ? p.sale_p : p.price_p} ₾</small>
              )}
            </RowItem>
          ))}
        </Results>
      )}
    </PickerWrap>
  );
}

// ───────────── Helpers: pricing

// ───────────── Error helper
const mlSupportsUnits = (ml) =>
  (ml?.stock_strategy ?? '') === 'units' || (ml?.unit_stock ?? null) != null;

// პროდუქტის დონეზე აღარ ვიყენებთ "any ML has units" ლოგიკას mode-ის გადასაწყვეტად

// helpers (კომპონენტის თავში ჩაამატე)
const canUnitMode = (p) =>
  p && (Array.isArray(p.milliliters) ? p.milliliters.some(mm => (mm?.unit_stock ?? null) != null) : false)
  || Boolean(p?.has_ml_unit_stock);

function formatAxiosError(err) {
  try {
    const status = err?.response?.status;
    const statusText = err?.response?.statusText || "";
    const url = err?.config?.url || "";
    const method = (err?.config?.method || "GET").toUpperCase();
    const msg = err?.message || "";

    let body = "";
    const data = err?.response?.data;
    if (data != null) {
      if (typeof data === "string") {
        body = data;
      } else {
        body = JSON.stringify(data, null, 2);
      }
    }

    // ზოგჯერ backend აბრუნებს HTML-ს — ამოვჭრათ მხოლოდ დასაწყისი
    if (body && body.length > 2000) body = body.slice(0, 2000) + "\n…(truncated)";

    // ზოგჯერ error ტექსტი მოდის data.error-ში ან data.detail-ში
    const serverMsg =
      (typeof data === "object" && (data?.error || data?.detail)) || "";

    const lines = [
      `Request: ${method} ${url}`,
      status ? `Status: ${status} ${statusText}` : null,
      serverMsg ? `Server error: ${serverMsg}` : null,
      `Message: ${msg}`,
      body ? `Response body:\n${body}` : null,
    ].filter(Boolean);

    return lines.join("\n");
  } catch (e) {
    return `Unexpected error: ${err?.message || String(err)}`;
  }
}

function unitPriceOf(item) {
  if (item.override_price !== "" && item.override_price !== null && !isNaN(item.override_price)) return Number(item.override_price);
  if (item.unit_price != null) return Number(item.unit_price);
  const p = item._product; if (!p) return 0;

  // accessories → price from chosen variant
  if (/accessor/i.test(String(p.category || ""))) {
    const variants = Array.isArray(p.accessory_variants) ? p.accessory_variants : [];
    let v = null;
    if (item.accessory_variant_id != null) v = variants.find(x => Number(x.id) === Number(item.accessory_variant_id));
    if (!v && variants.length) {
      const active  = variants.filter(x => Number(x.is_active ?? 1) === 1);
      const inStock = active.filter(x => Number(x.stock ?? 0) > 0);
      v = inStock[0] || active[0] || variants[0];
    }
    if (v) { const sale = Number(v.sale_p ?? 0); return sale > 0 ? sale : Number(v.price ?? 0); }
  }

  // ml-based
  const list = Array.isArray(p.milliliters) ? p.milliliters : [];
  if (list.length) {
    let ml = null;
    if (item.ml_id != null) ml = list.find(m => m.id != null && Number(m.id) === Number(item.ml_id));
    if (!ml && item.ml_size != null) ml = list.find(m => Number(m.size) === Number(item.ml_size));
    if (ml) return Number(ml.sale_price || ml.price) || 0;
  }

  if (p.sale_p && Number(p.sale_p) > 0) return Number(p.sale_p);
  return Number(p.price_p) || 0;
}
function subtotalOf(items) { return (items || []).reduce((sum, it) => sum + unitPriceOf(it) * (Number(it.quantity) || 0), 0); }
function finalPrice(mode, base_price, discount_value, itemsSubtotal) {
  if (mode === 'fixed') return Number(base_price || 0);
  if (mode === 'percent_off') return Math.max(0, +(itemsSubtotal * (1 - (Number(discount_value || 0) / 100))).toFixed(2));
  if (mode === 'amount_off') return Math.max(0, +(itemsSubtotal - Number(discount_value || 0)).toFixed(2));
  return itemsSubtotal;
}

// ───────────── Accessory helpers
const normalizeColor = (v) => (v?.color_en || v?.color_ka || v?.color || "").trim();
const normalizeSize  = (v) => (v?.size_label || v?.size || "").trim();
const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
const sortVariantsSmart = (vars) => {
  const n = (x) => (x==null?Infinity:Number(x));
  const d = (x) => (x ? Date.parse(x) || Infinity : Infinity);
  return [...vars].sort((a,b) => {
    const da = d(a.created_at), db = d(b.created_at); if (da !== db) return da - db;
    const sa = n(a.size_sort), sb = n(b.size_sort);   if (sa !== sb) return sa - sb;
    return n(a.id) - n(b.id);
  });
};

// ───────────── Main
export default function GiftSetsAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

const fetchList = async () => {
  setLoading(true);
  try {
    const { data } = await axios.get(`${API}/list.php`, {
      params: {
        q,
        _t: Date.now()
      },
      headers: {
        "Cache-Control": "no-cache",
        "Pragma": "no-cache"
      }
    });

    setList(Array.isArray(data) ? data : []);
  } catch (e) {
    console.error(e);
    setList([]);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => { fetchList(); }, []);

  const startCreate = () => {
    setEditing({
      id: null,
      gift_set_id: "",
      brand: "",
      note: "",
      note_en: "",
      full_stock: 0,
      method_of_use: "",
      method_of_use_en: "",
      purpose_problem: "",
      purpose_problem_en: "",
      product_type: "",
      product_type_en: "",
      use: "",
      use_en: "",
      sex: "",
      images: [],

      title_ka: "",
      title_en: "",
      description_ka: "",
      description_en: "",
      category: "perfume",
      price_mode: "fixed",
      base_price: "",
      discount_value: "",
      is_active: 1,
      sort_order: 100,
      items: [],
      seo_title_ka: "",
      seo_title_en: "",
      seo_desc_ka: "",
      seo_desc_en: "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEdit = async (id) => {
    try {
      const { data } = await axios.get(`${API}/read.php`, { params: { id } });
      const items = (data.items || []).map(it => ({
        product_id: String(it.product_id),
        quantity: Number(it.quantity) || 1,
        override_price: it.override_price ?? "",
        ml_id: it.ml_id ?? null,
        ml_size: it.ml_size ?? null,
        unit_price: it.unit_price ?? null,
        accessory_variant_id: it.accessory_variant_id ?? null,
        accessory_color: null,
        accessory_size: null,
        _product: null
      }));

      // hydrate products + derive accessory color/size
      await Promise.all(items.map(async (it) => {
        try {
          const { data: arr } = await axios.get(`${API}/search_products.php`, { params: { q: String(it.product_id), limit: 20 } });
          const found = (arr || []).find(p => String(p.id) === String(it.product_id));
          if (found) {
  it._product = found;

  if (it.ml_id == null && it.ml_size != null) {
    const m = (found.milliliters || []).find(mm => Number(mm.size) === Number(it.ml_size));
    if (m && m.id != null) it.ml_id = Number(m.id);
  }

  // ✅ აქ ჩავწეროთ perfume_mode, თუ ჯერ unset-ია
  if ((found.category || '').toLowerCase() === 'perfume') {
    const mlList = Array.isArray(found.milliliters) ? found.milliliters : [];
    const chosen = mlList.find(mm =>
      (it.ml_id != null && mm.id != null && Number(mm.id) === Number(it.ml_id)) ||
      (it.ml_id == null && it.ml_size != null && Number(mm.size) === Number(it.ml_size))
    ) || mlList[0];

    const supportsUnits = mlSupportsUnits(chosen);
    if (!it.perfume_mode) {
      it.perfume_mode = supportsUnits ? 'units' : 'full';
    }
  }

            if (/accessor/i.test(String(found.category)) && it.accessory_variant_id != null) {
              const vars = Array.isArray(found.accessory_variants) ? found.accessory_variants : [];
              const v = vars.find(x => Number(x.id) === Number(it.accessory_variant_id));
              if (v) {
                it.accessory_color = normalizeColor(v);
                it.accessory_size  = normalizeSize(v);
              }
            }
          }
        } catch {}
      }));

      setEditing({
        ...data,
        base_price: data.base_price ?? "",
        discount_value: data.discount_value ?? "",
        images: Array.isArray(data.images) ? data.images : [],
        gift_set_id: data.gift_set_id || "",
        brand: data.brand || "",
        note: data.note || "",
        note_en: data.note_en || "",
        full_stock: Number(data.full_stock || 0),
        method_of_use: data.method_of_use || "",
        method_of_use_en: data.method_of_use_en || "",
        purpose_problem: data.purpose_problem || "",
        purpose_problem_en: data.purpose_problem_en || "",
        product_type: data.product_type || "",
        product_type_en: data.product_type_en || "",
        use: data.use || "",
        use_en: data.use_en || "",
        sex: data.sex || "",
        items
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
  console.error(e?.response || e);
  alert("Failed to load set:\n\n" + formatAxiosError(e));
    }
  };

const remove = async (id) => {
  if (!window.confirm("Delete this gift set?")) return;

  const oldList = list;

  // მაშინვე გაქრეს UI-დან
  setList(prev => prev.filter(row => String(row.id) !== String(id)));

  try {
    await axios.post(
      `${API}/delete.php?_t=${Date.now()}`,
      { id },
      {
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache"
        }
      }
    );

    await fetchList();
  } catch (e) {
    setList(oldList);
    console.error(e?.response || e);
    alert("Delete failed:\n\n" + formatAxiosError(e));
  }
};

  // Image gallery
  const onPickFiles = async (ev) => {
    const files = Array.from(ev.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await axios.post(`${API}/upload_image.php`, fd);
        if (data?.success && data?.url) uploaded.push(data.url);
      }
      setEditing(ed => ({ ...ed, images: [...(ed.images||[]), ...uploaded] }));
    } catch (e) {
      console.error(e); alert("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // items ops
  const addRow = () => {
    setEditing((ed) => ({
      ...ed,
      items: [...(ed.items || []), {
        product_id: "", quantity: 1, override_price: "",
        ml_size: null, ml_id: null,
        accessory_variant_id: null, accessory_color: null, accessory_size: null,
        perfume_mode: 'full', // 'full' = მილილიტრაჟით (დეკანტი), 'unit' = ბოთლებით
        _product: null
      }]
    }));
  };
  const updateRow = (idx, patch) => {
    setEditing((ed) => { const items = [...(ed.items || [])]; items[idx] = { ...items[idx], ...patch }; return { ...ed, items }; });
  };
  const removeRow = (idx) => {
    setEditing((ed) => { const items = [...(ed.items || [])]; items.splice(idx, 1); return { ...ed, items }; });
  };

  // save
  const save = async () => {
    const payload = {
      ...editing,
      base_price: editing.price_mode === 'fixed' ? parseFloat(editing.base_price || 0) : null,
      discount_value: editing.price_mode !== 'fixed' ? parseFloat(editing.discount_value || 0) : null,
      gift_set_id: editing.gift_set_id || undefined,
      brand: editing.brand || null,
      note: editing.note || null,
      note_en: editing.note_en || null,
      full_stock: Number(editing.full_stock || 0),
      method_of_use: editing.method_of_use || null,
      method_of_use_en: editing.method_of_use_en || null,
      purpose_problem: editing.purpose_problem || null,
      purpose_problem_en: editing.purpose_problem_en || null,
      product_type: editing.product_type || null,
      product_type_en: editing.product_type_en || null,
      use: editing.use || null,
      use_en: editing.use_en || null,
      sex: editing.sex || null,
      images: Array.isArray(editing.images) ? editing.images : [],
      items: (editing.items || []).map(it => {
        const pid = String(it.product_id || it._product?.id || '').trim();
        if (!pid) return null;
        return {
          product_id: pid,
          quantity: Number(it.quantity) > 0 ? Number(it.quantity) : 1,
          override_price: (it.override_price !== '' && it.override_price !== null) ? Number(it.override_price) : null,
          ml_id: it.ml_id ?? null,
          ml_size: it.ml_size ?? null,
          accessory_variant_id: it.accessory_variant_id ?? null,
          perfume_mode: (String(it.perfume_mode || 'full') === 'units' ? 'units' : 'full')
        };
      }).filter(Boolean)
    };

    if (!payload.items.length) { alert("კოლექციაში მაინც ერთი პროდუქტი აირჩიე."); return; }

    try {
      const url = editing.id ? `${API}/update.php` : `${API}/create.php`;
       await axios.post(`${url}?_t=${Date.now()}`, payload, {
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache"
          }
        });

        setEditing(null);
        await fetchList();

    } catch (e) {
      console.error(e?.response || e);
      alert("Save failed:\n\n" + formatAxiosError(e));
    }
  };

  // derived
  const itemsSubtotal = useMemo(() => subtotalOf(editing?.items), [editing]);
  const totalPrice = useMemo(() => finalPrice(editing?.price_mode, editing?.base_price, editing?.discount_value, itemsSubtotal), [editing, itemsSubtotal]);

  // accessory color/size handlers
  const onAccessoryColorChange = (idx, color, item) => {
    const variants = Array.isArray(item._product?.accessory_variants) ? sortVariantsSmart(item._product.accessory_variants) : [];
    const sizesForColor = variants.filter(v => normalizeColor(v) === color).map(normalizeSize);
    const size = sizesForColor[0] || null;
    const variant = color && size ? variants.find(v => normalizeColor(v) === color && normalizeSize(v) === size) : null;
    updateRow(idx, { accessory_color: color || null, accessory_size: size, accessory_variant_id: variant?.id ?? null });
  };
  const onAccessorySizeChange = (idx, size, item) => {
    const variants = Array.isArray(item._product?.accessory_variants) ? sortVariantsSmart(item._product.accessory_variants) : [];
    const color = item.accessory_color || normalizeColor(variants[0] || {});
    const variant = variants.find(v => normalizeColor(v) === color && normalizeSize(v) === size) || null;
    updateRow(idx, { accessory_size: size || null, accessory_variant_id: variant?.id ?? null });
  };

  return (
    <Wrap>
      <Top>
        <H>Gift Sets</H>
        <div style={{display:"flex",gap:8}}>
          <Input placeholder="Search title…" value={q} onChange={(e)=>setQ(e.target.value)} />
          <Ghost onClick={fetchList}>Search</Ghost>
          <Btn onClick={startCreate}>+ New Gift Set</Btn>
        </div>
      </Top>

      {editing && (
        <Card>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12}}>
            <H style={{marginBottom:8}}>{editing.id ? "Edit Gift Set" : "Create Gift Set"}</H>
            <div style={{display:'grid',gap:4,textAlign:'right'}}>
              <Small>Subtotal: <b>{itemsSubtotal.toFixed(2)} ₾</b></Small>
              <Small>Final price: <b>{totalPrice.toFixed(2)} ₾</b></Small>
            </div>
          </div>

          <Field><label>Title (KA)</label><Input value={editing.title_ka} onChange={(e)=>setEditing({...editing,title_ka:e.target.value})}/></Field>
          <Field><label>Title (EN)</label><Input value={editing.title_en} onChange={(e)=>setEditing({...editing,title_en:e.target.value})}/></Field>

          <Field><label>Description (KA)</label><Textarea value={editing.description_ka} onChange={(e)=>setEditing({...editing,description_ka:e.target.value})}/></Field>
          <Field><label>Description (EN)</label><Textarea value={editing.description_en} onChange={(e)=>setEditing({...editing,description_en:e.target.value})}/></Field>

          <Field>
            <label>Category</label>
            <Select value={editing.category} onChange={(e)=>setEditing({...editing,category:e.target.value})}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
          </Field>

          <Field>
            <label>Pricing Mode</label>
            <Select value={editing.price_mode} onChange={(e)=>setEditing({...editing,price_mode:e.target.value})}>
              <option value="fixed">Fixed</option>
              <option value="percent_off">% Off</option>
              <option value="amount_off">Amount Off</option>
            </Select>
            <Small>
              {editing.price_mode === 'fixed' && "Final price is Base price."}
              {editing.price_mode === 'percent_off' && "Final = Sum(items) × (1 - %/100)."}
              {editing.price_mode === 'amount_off' && "Final = Sum(items) - Amount."}
            </Small>
          </Field>

          {/* Pricing inputs, based on mode */}
{editing.price_mode === "fixed" && (
  <Field>
    <label>Base Price (₾)</label>
    <Input
      type="number"
      step="0.01"
      min="0"
      value={editing.base_price}
      onChange={(e) =>
        setEditing({ ...editing, base_price: e.target.value })
      }
      placeholder="e.g. 129.90"
    />
    <Small>Final price იქნება ზუსტად ეს მნიშვნელობა.</Small>
  </Field>
)}

{editing.price_mode === "percent_off" && (
  <Field>
    <label>Discount (%)</label>
    <Input
      type="number"
      step="1"
      min="0"
      max="100"
      value={editing.discount_value}
      onChange={(e) =>
        setEditing({ ...editing, discount_value: e.target.value })
      }
      placeholder="e.g. 15"
    />
    <Small>Final = Sum(items) × (1 - %/100)</Small>
  </Field>
)}

{editing.price_mode === "amount_off" && (
  <Field>
    <label>Amount Off (₾)</label>
    <Input
      type="number"
      step="0.01"
      min="0"
      value={editing.discount_value}
      onChange={(e) =>
        setEditing({ ...editing, discount_value: e.target.value })
      }
      placeholder="e.g. 20"
    />
    <Small>Final = Sum(items) - Amount</Small>
  </Field>
)}


          {/* Images */}
          <Field>
            <label>Images (multiple)</label>
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} disabled={uploading}/>
              {uploading && <Small>Uploading…</Small>}
            </div>
            <div style={{marginTop:10,display:'flex',gap:10,flexWrap:'wrap'}}>
              {(editing.images || []).map((url,i)=>(
                <div key={url+i} style={{position:'relative',width:100}}>
                  <img src={url} alt="" style={{width:100,height:100,objectFit:'cover',borderRadius:8,border:'1px solid #eee'}}/>
                  <div style={{display:'flex',gap:6,marginTop:6}}>
                    <Ghost type="button" onClick={()=>{
                      setEditing(ed=>{const arr=[...ed.images]; if(i>0)[arr[i-1],arr[i]]=[arr[i],arr[i-1]]; return {...ed,images:arr};});
                    }}>↑</Ghost>
                    <Ghost type="button" onClick={()=>{
                      setEditing(ed=>{const arr=[...ed.images]; if(i<arr.length-1)[arr[i+1],arr[i]]=[arr[i],arr[i+1]]; return {...ed,images:arr};});
                    }}>↓</Ghost>
                    <Danger type="button" onClick={()=>{
                      setEditing(ed=>{const arr=[...ed.images]; arr.splice(i,1); return {...ed,images:arr};});
                    }}>Remove</Danger>
                  </div>
                  {i===0 && <Small>Cover</Small>}
                </div>
              ))}
            </div>
          </Field>

          {/* Items */}
          <Field>
            <label>Items</label>
            <ProductsTable>
              <div className="head">
                <div>Product</div><div>Qty</div><div>Variant / ML / Price</div><div>Override</div><div></div>
              </div>

              {(editing.items || []).map((it, idx) => {
                const isAcc = /accessor/i.test(String(it._product?.category || ""));
                const variants = isAcc ? sortVariantsSmart(it._product?.accessory_variants || []) : [];
                const colorList = isAcc ? uniq(variants.map(normalizeColor)) : [];
                const effectiveColor = it.accessory_color || colorList[0] || "";
                const sizesForColor = isAcc && effectiveColor
                  ? uniq(variants.filter(v => normalizeColor(v) === effectiveColor).map(normalizeSize))
                  : [];

                return (
                  <div key={idx}>
                    <div>
                      <ProductPicker
                        value={it._product}
onSelect={(p) => {
  const mls = Array.isArray(p.milliliters) ? p.milliliters.slice().sort((a,b)=>Number(a.size)-Number(b.size)) : [];
  const keepMl  = mls.find(m =>
    (it.ml_id != null && m.id != null && Number(m.id) === Number(it.ml_id)) ||
    (it.ml_id == null && it.ml_size != null && Number(m.size) === Number(it.ml_size))
  );
  const firstMl = mls[0] || null;

  const selectedMl = keepMl || firstMl;
  const nextPerfumeMode =
    (String(p.category||'').toLowerCase() === 'perfume' && mlSupportsUnits(selectedMl)) ? 'units' : 'full';

  // accessories defaults (როგორც გაქვს)
  let nextColor = null, nextSize = null, nextVariantId = null;
  if (/accessor/i.test(String(p.category || ""))) {
    const vars = Array.isArray(p.accessory_variants) ? sortVariantsSmart(p.accessory_variants) : [];
    if (vars.length) {
      if (it.accessory_variant_id != null) {
        const keep = vars.find(v => Number(v.id) === Number(it.accessory_variant_id));
        if (keep) {
          nextColor = normalizeColor(keep);
          nextSize  = normalizeSize(keep);
          nextVariantId = keep.id;
        }
      }
      if (nextVariantId == null) {
        const active  = vars.filter(v => Number(v.is_active ?? 1) === 1);
        const inStock = active.filter(v => Number(v.stock ?? 0) > 0);
        const pick = inStock[0] || active[0] || vars[0];
        nextColor = normalizeColor(pick);
        nextSize  = normalizeSize(pick);
        nextVariantId = pick?.id ?? null;
      }
    }
  }


  updateRow(idx, {
    product_id: String(p.id),
    _product: p,
    ml_id:  (selectedMl?.id ?? null) != null ? Number(selectedMl.id) : null,
    ml_size: (selectedMl?.size != null) ? Number(selectedMl.size) : null,
    accessory_color: nextColor,
    accessory_size: nextSize,
    accessory_variant_id: nextVariantId,
    perfume_mode: nextPerfumeMode,
  });
}}

                      />
                    </div>

                    <div>
                      <Input type="number" min="1" value={it.quantity}
                             onChange={(e)=>updateRow(idx,{quantity:Number(e.target.value || 1)})}/>
                    </div>

                    <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                      {/* accessories: color + size */}
                      {isAcc && variants.length > 0 && (
                        <>
                          <Select
                            value={effectiveColor}
                            onChange={(e)=>onAccessoryColorChange(idx, e.target.value, it)}
                          >
                            {(colorList.length ? colorList : [""]).map(c => (
                              <option key={c || "_"} value={c || ""}>{c || "—"}</option>
                            ))}
                          </Select>
                          <Select
                            value={it.accessory_size || sizesForColor[0] || ""}
                            onChange={(e)=>onAccessorySizeChange(idx, e.target.value, it)}
                          >
                            {(sizesForColor.length ? sizesForColor : [""]).map(s => (
                              <option key={s || "_"} value={s || ""}>{s || "—"}</option>
                            ))}
                          </Select>
                        </>
                      )}

                      {/* ml-based */}
                      {it._product && Array.isArray(it._product.milliliters) && it._product.milliliters.length > 0 && (
                        <Select
                          value={String(it.ml_id ?? it.ml_size ?? "")}
onChange={(e)=>{
  const val = e.target.value;
  const list = it._product?.milliliters || [];
  const match = list.find(mm => String(mm.id ?? mm.size) === val);

  const next = {
    ml_id: match && match.id != null ? Number(match.id) : null,
    ml_size: match ? Number(match.size) : null
  };

  // არჩეული ML თუ units-ს უჭერს მხარს -> დააყენე 'units', სხვა შემთხვევაში 'full'
  const supportsUnits = mlSupportsUnits(match);
  next.perfume_mode = supportsUnits ? 'units' : 'full';

  updateRow(idx, next);
}}
                        >
                          {(it._product?.milliliters || []).slice().sort((a,b)=>Number(a.size)-Number(b.size)).map(m => {
                            const pr = Number(m.sale_price || m.price) || 0;
                            const optVal = String(m.id ?? m.size);
                            return <option key={optVal} value={optVal}>{m.size} ml — {pr} ₾</option>;
                          })}
                        </Select>
                      )}

{it._product && (it._product.category || '').toLowerCase() === 'perfume' &&
 Array.isArray(it._product.milliliters) && it._product.milliliters.length > 0 && (
  (() => {
    const mlList = it._product.milliliters || [];
    const chosen = mlList.find(mm =>
      (it.ml_id != null && mm.id != null && Number(mm.id) === Number(it.ml_id)) ||
      (it.ml_id == null && it.ml_size != null && Number(mm.size) === Number(it.ml_size))
    ) || mlList[0];

const canUnitsHere = mlSupportsUnits(chosen);
const current = it.perfume_mode || (canUnitsHere ? 'units' : 'full');

// ✅ თუ არ ეწერა, ერთჯერადად ჩავწეროთ რათა save()-მდე არ დაიკარგოს
if (!it.perfume_mode) {
  updateRow(idx, { perfume_mode: current });
}

    return (
      <Select
        value={current}
        onChange={(e)=>updateRow(idx,{ perfume_mode: e.target.value })}
        title="Stock mode for selected ML"
      >
        {canUnitsHere && <option value="units">Bottle units (unit_stock)</option>}
        <option value="full">Full (ml decant)</option>
      </Select>
    );
  })()
)}


                      <Small>Unit: <b>{unitPriceOf(it)} ₾</b></Small>
                    </div>

                    <div>
                      <Input type="number" step="0.01" placeholder="(optional)"
                             value={it.override_price}
                             onChange={(e)=>updateRow(idx,{override_price:e.target.value})}/>
                    </div>

                    <div style={{display:'flex',justifyContent:'flex-end'}}>
                      <Danger type="button" onClick={()=>removeRow(idx)}>Remove</Danger>
                    </div>
                  </div>
                );
              })}
            </ProductsTable>
            <div style={{marginTop:8,display:'flex',gap:8}}>
              <Ghost type="button" onClick={addRow}>+ Add item</Ghost>
            </div>
          </Field>

          <Field>
            <label>Active</label>
            <Select value={editing.is_active} onChange={(e)=>setEditing({...editing,is_active:Number(e.target.value)})}>
              <option value={1}>Active</option>
              <option value={0}>Inactive (Draft)</option>
            </Select>
          </Field>

          <Field>
            <label>Sort Order</label>
            <Input type="number" value={editing.sort_order} onChange={(e)=>setEditing({...editing,sort_order:Number(e.target.value)})}/>
          </Field>

          <Card style={{background:"#fafafa"}}>
            <H style={{fontSize:"1.1rem"}}>Gift Set Meta (optional)</H>
            <Field><label>Brand</label><Input value={editing.brand || ""} onChange={(e)=>setEditing({...editing,brand:e.target.value})}/></Field>
            <Field><label>Full stock</label><Input type="number" value={editing.full_stock ?? 0} onChange={(e)=>setEditing({...editing,full_stock:Number(e.target.value||0)})}/></Field>
            <Field><label>Sex</label>
              <Select value={editing.sex || ""} onChange={(e)=>setEditing({...editing,sex:e.target.value})}>
                <option value="">—</option><option value="men">Men</option><option value="women">Women</option><option value="unisex">Unisex</option>
              </Select>
            </Field>
            <Field><label>Note (KA)</label><Textarea value={editing.note || ""} onChange={(e)=>setEditing({...editing,note:e.target.value})}/></Field>
            <Field><label>Note (EN)</label><Textarea value={editing.note_en || ""} onChange={(e)=>setEditing({...editing,note_en:e.target.value})}/></Field>
            <Field><label>Method of use (KA)</label><Textarea value={editing.method_of_use || ""} onChange={(e)=>setEditing({...editing,method_of_use:e.target.value})}/></Field>
            <Field><label>Method of use (EN)</label><Textarea value={editing.method_of_use_en || ""} onChange={(e)=>setEditing({...editing,method_of_use_en:e.target.value})}/></Field>
            <Field><label>Purpose / Problem (KA)</label><Textarea value={editing.purpose_problem || ""} onChange={(e)=>setEditing({...editing,purpose_problem:e.target.value})}/></Field>
            <Field><label>Purpose / Problem (EN)</label><Textarea value={editing.purpose_problem_en || ""} onChange={(e)=>setEditing({...editing,purpose_problem_en:e.target.value})}/></Field>
            <Field><label>Product type (KA)</label><Input value={editing.product_type || ""} onChange={(e)=>setEditing({...editing,product_type:e.target.value})}/></Field>
            <Field><label>Product type (EN)</label><Input value={editing.product_type_en || ""} onChange={(e)=>setEditing({...editing,product_type_en:e.target.value})}/></Field>
            <Field><label>Use (KA)</label><Input value={editing.use || ""} onChange={(e)=>setEditing({...editing,use:e.target.value})}/></Field>
            <Field><label>Use (EN)</label><Input value={editing.use_en || ""} onChange={(e)=>setEditing({...editing,use_en:e.target.value})}/></Field>
          </Card>

          <Card style={{background:"#fafafa"}}>
            <H style={{fontSize:"1.1rem"}}>SEO (optional)</H>
            <Field><label>SEO Title (KA)</label><Input value={editing.seo_title_ka || ""} onChange={(e)=>setEditing({...editing,seo_title_ka:e.target.value})}/></Field>
            <Field><label>SEO Title (EN)</label><Input value={editing.seo_title_en || ""} onChange={(e)=>setEditing({...editing,seo_title_en:e.target.value})}/></Field>
            <Field><label>SEO Desc (KA)</label><Textarea value={editing.seo_desc_ka || ""} onChange={(e)=>setEditing({...editing,seo_desc_ka:e.target.value})}/></Field>
            <Field><label>SEO Desc (EN)</label><Textarea value={editing.seo_desc_en || ""} onChange={(e)=>setEditing({...editing,seo_desc_en:e.target.value})}/></Field>
          </Card>

          <div style={{display:"flex",gap:8}}>
            <Ok type="button" onClick={save}>{editing.id ? "Update" : "Create"}</Ok>
            <Ghost type="button" onClick={()=>setEditing(null)}>Cancel</Ghost>
          </div>
        </Card>
      )}

<Grid>
  <Row>
    <div>ID</div>
    <div>Title</div>
    <div>Brand</div>
    <div>Price</div>
    <div>Stock</div>
    <div>Category</div>
    <div>Items</div>
    <div>Actions</div>
  </Row>

  {loading ? (
    <Row>
      <div>Loading…</div>
    </Row>
  ) : list.length ? (
    list.map((row) => (
      <Row key={row.id}>
        <div>{row.id}</div>

        <div>
          <div style={{ fontWeight: 900 }}>
            {row.title_en || row.title_ka || "—"}
          </div>
          <Small>{row.title_ka || "—"}</Small>
          {row.gift_set_id && <Small>Code: {row.gift_set_id}</Small>}
        </div>

        <div>{row.brand || "—"}</div>

        <div>
          <b>
            {Number(row.final_price || row.base_price || 0).toFixed(2)} ₾
          </b>

          {Number(row.compare_price || 0) > 0 && (
            <Small>
              Old: {Number(row.compare_price).toFixed(2)} ₾
            </Small>
          )}
        </div>

        <div>
          <b>{row.full_stock ?? 0}</b>
        </div>

        <div>
          <Tag>{row.category || "—"}</Tag>
          {row.sex && <Small>{row.sex}</Small>}
        </div>

        <div>{row.items_count || 0}</div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Ghost onClick={() => startEdit(row.id)}>Edit</Ghost>
          <Danger onClick={() => remove(row.id)}>Delete</Danger>
        </div>
      </Row>
    ))
  ) : (
    <Row>
      <div>No gift sets yet.</div>
    </Row>
  )}
</Grid>

    </Wrap>
  );
}
